import React, { useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, shape, number, client, extend, useDataModel, arrayOf
} from '@thd-nucleus/data-sources';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import THDStorageUtils, { COOKIE_STORAGE } from '@thd-olt-global/thd-storage-utils';
import { DrawerHeader } from '@one-thd/sui-atomic-components';
import { AddToContext } from '../../AddToContext';
import { useListDetails, useUpdateList, listDetailsModel, updateListModel, createListModel } from '../../hooks/useLists';
import { SCREENS, OPERATION_ADD_ITEMS } from '../../AddToConstants';
import { SignIn } from '../../common/addto-signin/SignIn';
import { CreateList } from './CreateList';
import { SelectList } from './SelectList';
import { AddToListSuccess } from './AddToListSuccess';
import { AddToLoading } from '../../common/addto-loading/AddToloading';
import { DefaultScreen } from '../../common/DefaultScreen';
import { MiniPip } from '../../common/mini-pip/MiniPip';
import { useBenefitCheck } from '../../hooks/useBenefitCheck';

export const AddToList = (props) => {
  const {
    products, showCount, skipListId, isSharedList, listId, onMaterialsAdded
  } = props;
  const theme = useTheme(AddToList, props);
  const { disableSignInDrawer } = theme.props;
  const { hideCount, showPlusSign } = theme.props;
  const {
    addToType,
    storeId,
    setScreen,
    screen,
    handleClose,
    selectedProductDetails,
    setShowSuccessMessage,
    setShowErrorMessage
  } = useContext(AddToContext);
  const { svocID, userID, customerType, isLoggedIn } = useThdCustomer() || {};
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [newListDetails, setNewListDetails] = useState({
    listName: '', listId: ''
  });
  const [keyword, setKeyword] = useState('');
  const {
    LIST_CREATE_LIST,
    LIST_SELECT_LIST,
    SIGN_IN,
    LIST_SUCCESS,
    LOADING,
    DEFAULT,
    MINI_PIP_BASE,
    MINI_PIP_PRODUCT_OVERVIEW,
    MINI_PIP_PRODUCT_SPECS,
    MINI_PIP_PRODUCT_REVIEWS
  } = SCREENS;
  let storeFrontSVOCID;
  let storeFrontUserID;

  const itemId = selectedProductDetails?.itemId || products[0]?.itemId;

  const { data } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId || products?.length > 1
  });

  const productData = data?.product || {};
  const productValue = productData?.pricing?.value;
  const identifiers = productData?.identifiers;
  const primaryImage = productData?.media?.images?.find(
    (img) => img.type === 'IMAGE' && img.subType === 'PRIMARY'
  );

  const storefrontCookieData = THDStorageUtils.get(COOKIE_STORAGE, { key: 'THD_STOREFRONT' });

  if (storefrontCookieData && storefrontCookieData !== 'undefined') {
    const storefrontPayload = storefrontCookieData.split('.')[1];
    /* istanbul ignore next */
    if (storefrontPayload) {
      const decodedStorefrontPayload = Buffer.from(storefrontPayload, 'base64').toString();
      const { SVOCID } = JSON.parse(decodedStorefrontPayload);
      storeFrontSVOCID = SVOCID;
      storeFrontUserID = (storeFrontSVOCID + '_STOREFRONT');
    }
  }

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('AddToListDrawer.ready');
  }, []);

  const { listData, listLoading, listRefetch } = useListDetails({
    customerAccountID: storeFrontSVOCID || svocID,
    userId: storeFrontSVOCID ? storeFrontUserID : userID,
    customerType: storeFrontSVOCID ? null : customerType?.toUpperCase(),
    skip: storeFrontSVOCID
      // eslint-disable-next-line max-len
      ? ((!storeFrontSVOCID || !storeFrontUserID) || addToType !== 'List') : ((!svocID || !userID) || addToType !== 'List')
  });

  const { listDetails: listDetailsData = null } = listData?.listDetails?.listResponse || {};
  const listDetails = (listDetailsData || []).filter((list) => list.listId !== skipListId);
  const listsExist = !!listDetails?.length;

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('AddToListDrawer.ready');
  }, []);

  const onCreateList = () => {
    listRefetch();
  };

  const { updateList, updateListResponse } = useUpdateList({
    id: listId,
    operationName: OPERATION_ADD_ITEMS,
  });

  const favoriteDetailCount = productData?.favoriteDetail?.count;

  useEffect(() => {
    setFavoriteCount(favoriteDetailCount);
  }, [favoriteDetailCount]);

  const shouldShowSelectList = useMemo(() => {
    return listsExist;
  }, [listLoading]);

  useEffect(() => {
    if (!disableSignInDrawer && !isLoggedIn) {
      setScreen(SIGN_IN);
    } else if (listLoading) {
      setScreen(LOADING);
    } else if (products.length === 0) {
      setScreen(DEFAULT);
    } else if (shouldShowSelectList) {
      setScreen(LIST_SELECT_LIST);
    } else if (screen !== LIST_SUCCESS) {
      setScreen(LIST_CREATE_LIST);
    }
  }, [isLoggedIn, listsExist, listLoading]);

  useEffect(() => {
    if (updateListResponse?.error) {
      setShowErrorMessage(true);
    }
    if (updateListResponse?.data?.updateList?.id) {
      if (onMaterialsAdded) {
        onMaterialsAdded();
      }
      setShowSuccessMessage(true);
    }
  }, [updateListResponse.error, updateListResponse.data]);

  const onSearch = (searchKeyword) => {
    setKeyword(searchKeyword);
  };

  const handleBackPress = () => {
    if (screen === LIST_CREATE_LIST) {
      setScreen(LIST_SELECT_LIST);
    }
    if (screen === MINI_PIP_BASE) {
      setScreen(DEFAULT);
    };
    // eslint-disable-next-line max-len
    if (screen === MINI_PIP_PRODUCT_OVERVIEW || screen === MINI_PIP_PRODUCT_SPECS || screen === MINI_PIP_PRODUCT_REVIEWS) {
      setScreen(MINI_PIP_BASE);
    }
  };

  const headerTitle = {
    LIST_CREATE_LIST: 'Create List',
    LIST_SELECT_LIST: 'Select List',
    DEFAULT: 'Add Materials',
    MINI_PIP_PRODUCT_REVIEWS: 'Add Materials',
    MINI_PIP_PRODUCT_SPECS: 'Add Materials',
    MINI_PIP_PRODUCT_OVERVIEW: 'Add Materials',
    MINI_PIP_BASE: 'Add Materials',
  }

  return (
    <>
     {(screen === LIST_SELECT_LIST && products?.length) || (screen === DEFAULT) || (screen === LIST_CREATE_LIST && !listsExist) ? (
       <DrawerHeader
       data-testid="add-to-drawer-header"
       title={headerTitle[screen]}
       onClose={handleClose}
     />
     ) : (
      <DrawerHeader
      data-testid="add-to-drawer-header"
      title={screen === LIST_CREATE_LIST ? 'Create List' : 'Add to List'}
      onClose={handleClose}
      onBack={handleBackPress}
    />
     ) }
      {screen === SIGN_IN && (
        <SignIn
          onClose={handleClose}
        />
      )}
      {screen === LOADING && (
        <AddToLoading />
      )}
      {screen === DEFAULT && (
        <DefaultScreen
          products={products}
          keyword={keyword}
          listsExist={listsExist}
          onSearch={onSearch}
          newListDetails={newListDetails}
          handleClose={handleClose}
          listId={listId}
          updateList={updateList}
          customerType={customerType}
        />
      )}
      {screen === LIST_CREATE_LIST && (
        <CreateList
          onClose={handleClose}
          onCreate={onCreateList}
          products={products}
          isExistingLists={listsExist}
          isSharedList={isSharedList}
          setFavoriteCount={setFavoriteCount}
          favoriteCount={favoriteCount}
          productValue={productValue}
          identifiers={identifiers}
          primaryImage={primaryImage}
          setNewListDetails={setNewListDetails}
        />
      )}
      {screen === LIST_SELECT_LIST && (
        <SelectList
          products={products}
          listDetails={listDetails}
          onClose={handleClose}
          isSharedList={isSharedList}
          setFavoriteCount={setFavoriteCount}
          favoriteCount={favoriteCount}
          productValue={productValue}
          identifiers={identifiers}
          primaryImage={primaryImage}
          setNewListDetails={setNewListDetails}
        />
      )}
      {screen === LIST_SUCCESS && (
        <AddToListSuccess
          newListDetails={newListDetails}
          onClose={handleClose}
        />
      )}
      {
        screen?.startsWith('MINI_PIP') && (
          <MiniPip
            itemId={itemId}
            updateList={updateList}
          />
        )
      }
    </>
  );
};

AddToList.displayName = 'AddToList';

AddToList.propTypes = {
  isSharedList: PropTypes.bool,
  listId: PropTypes.string,
  onMaterialsAdded: PropTypes.func,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  })),
  showCount: PropTypes.bool,
  skipListId: PropTypes.bool
};

AddToList.defaultProps = {
  isSharedList: false,
  listId: '',
  onMaterialsAdded: () => { },
  products: [],
  showCount: false,
  skipListId: false
};

const favoriteDetailCount = shape({
  count: number()
});

if (favoriteDetailCount.skip) {
  favoriteDetailCount.skip('skipFavoriteCount', false);
}

AddToList.dataModel = extend(
  {
    product: params({ itemId: string().isRequired(), dataSource: string() }).shape({
      itemId: string(),
      dataSource: string(),
      dataSources: string(),
      favoriteDetail: client(favoriteDetailCount),
      pricing: params({ storeId: string() }).shape({
        value: number({ float: true })
      }),
      identifiers: shape({
        brandName: string(),
        productLabel: string()
      }),
      media: shape({
        images: arrayOf(shape({
          url: string(),
          type: string(),
          subType: string()
        }))
      }),
    })
  },
  listDetailsModel,
  updateListModel,
  createListModel,
  useBenefitCheck
);